import { type JanusCtx, type JanusReaders, type Row, activeStatuses, ecId, ecLean, isInventoryRow, itemField, record, requiredInt, upper, insertRoll } from './context.js';
import { type JanusInput } from './janus.js';
import { checkOpposingTeam } from './check-opposing-team.js';
import { checkAbilitySizeCompatibility, SIZE_RANK } from './check-ability-size-compatibility.js';
import { isAuraEffect } from './aoe/expand-self-follow-aura.js';
import { applyAbilityEffectToTarget } from './apply-ability-effect-to-target.js';
import { layerListEntryUuid } from './statuses/status-trigger-gate.js';
import { statusItemId } from '../statuses/expiry.js';
import { gateItemPath } from './immune-to-ability.js';
import { resolveObjectVitality } from '../inventory/object-vitality.js';

const DEAD = '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA';
const CONTEST = 'AD80D31D-898B-66CE-0894-1335D11B1529';
const HEALING_TYPES = new Set(['4FB7452A-F130-40C3-83E6-5E92C329D7B2', '9909BE44-99AE-8780-BB12-BD99BA6E672E',
  '5E2689A2-0F7D-1E27-F4D5-CA09CA5C2591', '4634635E-4E82-DFB3-8411-27A6BFB8CF0F']);
const TEMP_MAX_V = 'F5BC82EE-DBDF-4594-F850-0C49E4292AC7';
export const DEAD_DAMAGE_WARNING = 'A dead creature has been targeted by a damage effect, its already dead!';
export const DEAD_HEAL_WARNING = 'A dead creature cannot be healed or regenerated — revive it first.';
const filled = (value: unknown): boolean => value != null && value !== '' && value !== 0 && value !== '0' && value !== false;

/** One warning per request/volley. Ordinary target skips are quiet inside triggers; unsupported riders can name their refusal. */
export function targetGateWarning(ctx: JanusCtx, message: string, options: { includeTriggers?: boolean } = {}): void {
  if ((ctx.trigger && !options.includeTriggers) || ctx.dryRun) return;
  const warnings = ctx.warnings ??= [];
  if (!warnings.includes(message)) warnings.push(message);
}

type DeadTargetReaders = Pick<JanusReaders, 'value' | 'entries' | 'deadState' | 'statuses'>;
function deadDamageTargetSkipReason(read: Pick<DeadTargetReaders, 'value' | 'deadState'>, ability: Row, target: Row,
  postEffectConditionsMet: boolean): string | null {
  if (!ecId(target) || !filled(read.value(ability, 'damage_type')) || postEffectConditionsMet) return null;
  return requiredInt(ecLean(target, 'encounter_creature_dead') ?? read.deadState(target), 'is_target_valid_for_effect:dead-state-not-held')
    ? DEAD_DAMAGE_WARNING : null;
}

/** Shared with participant hydration: these skips need saved death/status rows, never the target's compile.
 * The resolver supplies its working statuses so earlier effects in the same cast remain visible. */
export function deadEffectTargetSkipReason(read: DeadTargetReaders, ability: Row, target: Row, postEffectConditionsMet = false,
  statuses = read.statuses): string | null {
  const damage = deadDamageTargetSkipReason(read, ability, target, postEffectConditionsMet);
  if (damage) return damage;
  const rollType = upper(read.value(ability, 'roll_type'));
  if ((!HEALING_TYPES.has(rollType) && rollType !== TEMP_MAX_V) || !ecId(target)) return null;
  const dead = requiredInt(ecLean(target, 'encounter_creature_dead') ?? read.deadState(target), 'is_target_valid_for_effect:dead-state-not-held') !== 0
    || statuses(target).some(status => statusItemId(status) === DEAD);
  // Dead is Maimed's overflow, not a leveled alias.
  const revives = read.entries(ability, 'status_effect_removed_from_target_after_cast')
    .some(entry => upper(layerListEntryUuid(entry)) === DEAD);
  return dead && !revives ? DEAD_HEAL_WARNING : null;
}

export function deadDamageTargetSkips(ctx: JanusCtx, ability: Row, target: Row, postEffectConditionsMet = false): boolean {
  if (isInventoryRow(target) && filled(ctx.read.value(ability, 'damage_type'))) {
    const vitality = resolveObjectVitality(target, ctx.encounter, ctx.read.definition);
    // Unknown vitality still takes the normal fact-read lane. A held zero is already destroyed.
    if (vitality && Number(vitality.v_current) <= 0) return true;
  }
  const warning = deadDamageTargetSkipReason(ctx.read, ability, target, postEffectConditionsMet);
  if (warning) targetGateWarning(ctx, warning);
  return warning !== null;
}

/** ft_is_target_valid_for_effect_gate, in PHP order. A skip consumes neither target dice nor target effects. */
export function isTargetValidForEffect(ctx: JanusCtx, ability: Row, caster: Row, target: Row,
  primary = false, postEffectConditionsMet = false): 'proceed' | 'skip' | 'aura_special' {
  const inventory = isInventoryRow(target);
  if (inventory && deadDamageTargetSkips(ctx, ability, target, postEffectConditionsMet)) return 'skip';
  const rider = record(ability.attack_ability_arr);
  const flag = (key: string): boolean => filled(ctx.read.value(ability, key)) || (!!rider && filled(ctx.read.value(rider, key)));
  if ((itemField(target, 'item_type') === 'lair' || ecLean(target, 'item_type') === 'lair') && ecId(target) !== ecId(caster)) return 'skip';
  if (flag('ability_only_affects_inventory') && !inventory) return 'skip';
  if (flag('effect_only_affects_your_summons')) {
    const controller = upper(ecLean(target, 'controller_encounter_creature_id'));
    if (!ecId(caster) || !controller || controller !== ecId(caster)) return 'skip';
  }
  if (primary && isAuraEffect(ctx, ability) && filled(ctx.read.value(ability, 'effect_only_affects_opposing_team_type'))
      && ecId(target) && !checkOpposingTeam(ctx, caster, target)) return 'aura_special';
  if (filled(ctx.read.value(ability, 'boon')) && inventory) return 'proceed';
  const deadWarning = deadEffectTargetSkipReason(ctx.read, ability, target, postEffectConditionsMet, row => activeStatuses(ctx, row));
  if (deadWarning) { targetGateWarning(ctx, deadWarning); return 'skip'; }
  const rollType = upper(ctx.read.value(ability, 'roll_type'));
  const healing = HEALING_TYPES.has(rollType);
  const opposingOnly = flag('effect_only_affects_opposing_team_type') || flag('target_opposing_team_only');
  if (inventory && (opposingOnly || filled(ctx.read.value(ability, 'target_opposing_team_only')))) return 'skip';
  if (opposingOnly && ecId(target) && !checkOpposingTeam(ctx, caster, target)) return 'skip';
  const sameOnly = flag('effect_only_affects_same_team_type');
  if (sameOnly && checkOpposingTeam(ctx, caster, target)) return 'skip';
  const canHealInventory = filled(ctx.read.value(ability, 'effect_can_heal_inventory'));
  const onlyHealInventory = filled(ctx.read.value(ability, 'effect_can_only_heal_inventory'));
  if (sameOnly && inventory && !(healing && (canHealInventory || onlyHealInventory))) return 'skip';
  if (healing && ((onlyHealInventory && !canHealInventory && ecId(target)) || (!canHealInventory && !onlyHealInventory && inventory))) return 'skip';
  if (filled(ctx.read.value(target, 'cannot_be_targeted_by_a_contesting_roll_ability')) && rollType === CONTEST && !filled(ctx.read.value(ability, 'aoe_type'))) {
    targetGateWarning(ctx, 'This target cannot be targeted by a non-AOE contesting roll spell, ability, or effect!');
    return 'skip';
  }
  if (!checkAbilitySizeCompatibility(ctx, ability, caster, target)) {
    const above = ctx.read.value(ability, 'ability_only_works_against_size_category_number_above');
    const below = above == null;
    const key = `ability_only_works_against_size_category_number_${below ? 'below' : 'above'}`;
    const delta = requiredInt(ctx.read.value(ability, key), 'check_ability_size_compatibility:invalid-size-delta');
    if (!ctx.trigger) {
      const casterSize = upper(ctx.read.value(caster, 'size')), targetSize = upper(ctx.read.value(target, 'size'));
      const anchor = SIZE_RANK[Math.max(0, Math.min(SIZE_RANK.length - 1, SIZE_RANK.indexOf(casterSize) + delta))];
      const token = `ft_size_gate:${casterSize}|${targetSize}|${anchor}|${below ? 'below' : 'above'}`;
      insertRoll(ctx, { roll_type_1: 'post_effect_condition_failed', roll_type_2: key, roll_item_id: String(itemField(ability, 'item_id') ?? ''),
        by: caster, to: target, roll_result: delta, rolls_full: [token, gateItemPath(ctx, ability)].filter(Boolean).join('\n') });
      ctx.rows[ctx.rows.length - 1]!.roll_private = 0;
    }
    targetGateWarning(ctx, `${String(itemField(ability, 'item_name') ?? 'This ability')} cannot affect a target ${below ? 'this much larger than the caster.' : 'this size relative to the caster.'}`);
    return 'skip';
  }
  return 'proceed';
}

/** Every roll handler's aura_special arm applies only the ability-as-status, then continues the target loop. */
export function effectTargetProceeds(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, primary = false): boolean {
  const valid = isTargetValidForEffect(ctx, ability, caster, target, primary);
  if (valid === 'aura_special') applyAbilityEffectToTarget(ctx, input, ability, caster, target);
  return valid === 'proceed';
}

/** Peek only through leading skips before handler setup, then gate later recipients as the loop reaches them.
 * This preserves PHP row order and lets an earlier target's effects change a later target's eligibility. */
export function effectTargets(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): Iterable<Row> | null {
  const iterator = (function* (): Generator<Row> {
    for (const target of targets) if (effectTargetProceeds(ctx, input, ability, caster, target, target === targets[0])) yield target;
  })();
  const first = iterator.next();
  return first.done ? null : { *[Symbol.iterator](): Generator<Row> { yield first.value; yield* iterator; } };
}
